// src/pages/designer/DesignerProgram.jsx
import React, { useEffect, useState } from "react";
import { useAuth } from "../../hooks/useAuth";
import { getSettings, tierPlainText } from "../../lib/db";
import { DetailCard, SectionLabel, ProgressBar, Spinner, CommissionPctFigures } from "../../components/UI";

export default function DesignerProgram() {
  const pagePad = "var(--page-pad-x)";
  const { profile, role } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState("");

  useEffect(() => {
    getSettings()
      .then((s) => { setSettings(s); setLoadError(""); })
      .catch((err) => {
        console.error(err);
        setLoadError("Could not load program settings. Check your connection or try again.");
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading || !settings) {
    if (!loading && loadError) {
      return (
        <div style={{ padding: 48, maxWidth: 520 }}>
          <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 28, fontWeight: 400 }}>Program Details</h1>
          <p style={{ fontSize: 14, color: "var(--mid)", marginTop: 12 }}>{loadError}</p>
        </div>
      );
    }
    return <Spinner />;
  }

  const tiers      = settings.tiers;
  const currentTier = tierPlainText(profile?.tier);

  return (
    <div>
      <div style={{ padding:`28px ${pagePad} 0` }}>
        <h1 style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:36, fontWeight:300 }}>Program Details</h1>
        <p style={{ fontSize:13, color:"var(--mid)", marginTop:4 }}>
          How commissions work and how to move up
          {role === "contractor" ? " (contractors use the same tiers and rates as designers)." : "."}{" "}
          Your progress counters reset each program year from your registration anniversary (shown on your dashboard).
        </p>
      </div>
      <div style={{ padding:`var(--page-pad-y) ${pagePad} 32px` }}>
        <SectionLabel>Commission Tiers</SectionLabel>
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:16, marginBottom:24 }}>
          {tiers.map((tier, i) => {
            const isCurrent = tier.name === currentTier;
            return (
              <div key={i} style={{ background:"var(--warm-white)", border:`1px solid ${isCurrent?"var(--gold)":"var(--border)"}`, borderRadius:4, padding:22, position:"relative" }}>
                {isCurrent && <div style={{ position:"absolute", top:12, right:14, fontSize:9, fontWeight:500, letterSpacing:"0.12em", textTransform:"uppercase", color:"var(--gold)", background:"var(--gold-pale)", padding:"3px 8px", borderRadius:2 }}>Your Tier</div>}
                <div style={{ fontSize:9, fontWeight:500, letterSpacing:"0.18em", textTransform:"uppercase", color:"var(--gold)", marginBottom:8 }}>Tier {i+1}</div>
                <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:400, marginBottom:4 }}>{tier.name}</div>
                <div style={{ display: "flex", alignItems: "baseline", flexWrap: "wrap", gap: "6px 10px", lineHeight: 1 }}>
                  <CommissionPctFigures rate={tier.rate} fontSize={38} />
                  <span style={{ fontSize: 14, fontFamily: "'Jost', sans-serif", fontWeight: 400, color: "var(--mid)" }}>commission</span>
                </div>
                <div style={{ fontSize:11, color:"var(--mid)", marginTop:8 }}>
                  ${tier.minSales.toLocaleString()}
                  {tier.maxSales ? ` – $${tier.maxSales.toLocaleString()}` : "+"} annual sales
                </div>
                {tier.tierReachBonus ? (
                  <div style={{
                    fontSize: 11,
                    color: "var(--charcoal)",
                    marginTop: 10,
                    paddingTop: 10,
                    borderTop: "1px solid var(--border)",
                    lineHeight: 1.5,
                  }}>
                    <span style={{ fontWeight: 600, fontFamily: "'Jost', sans-serif", letterSpacing: "0.06em", textTransform: "uppercase", fontSize: 9, color: "var(--gold)" }}>Bonus at this tier</span>
                    <div style={{ marginTop: 6 }}>{tier.tierReachBonus}</div>
                  </div>
                ) : null}
                {isCurrent && profile?.ytdSales !== undefined && tiers[i+1] && (
                  <div style={{ marginTop:12 }}>
                    <ProgressBar
                      value={profile.ytdSales}
                      max={tiers[i+1].minSales}
                      label={`$${Math.max(0, tiers[i+1].minSales - profile.ytdSales).toLocaleString()} to ${tiers[i+1].name} tier`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <SectionLabel>Program Rules</SectionLabel>
        <DetailCard rows={[
          ["Payment Schedule",    settings.paymentSchedule],
          ["Minimum Payout",      `$${settings.minimumPayout} (held until threshold met)`],
          ["Commission Trigger",  settings.commissionTrigger],
          ["Return Policy",       settings.returnPolicy],
          ["Tax Forms",           settings.taxFormNote],
          ["Questions",           <a href={`mailto:${settings.contactEmail}`} style={{color:"var(--gold)"}}>{settings.contactEmail}</a>],
        ]} />

        <div style={{ background:"var(--charcoal)", borderRadius:4, padding:"24px 28px", marginTop:20 }}>
          <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:300, fontStyle:"italic", color:"var(--cream)", marginBottom:8 }}>Questions about your account?</div>
          <p style={{ fontSize:13, color:"rgba(245,240,232,0.55)", lineHeight:1.7 }}>
            Our showroom team is happy to help with commission questions, order tracking, or referral introductions.<br />
            <a href={`mailto:${settings.contactEmail}`} style={{ color:"var(--gold)" }}>{settings.contactEmail}</a>
          </p>
        </div>
      </div>
    </div>
  );
}
